<template>
  <div class="contact-form" id="contact">
    <div class="section-title limit-width">
      {{ __('reserve_appointment') }}
    </div>
    <form class="contact-form-box" @submit.prevent="submitForm" novalidate>
      <div class="contact-form-box-section limit-width">
        <p
          class="body-text-bold"
          :class="{
            'error-text':
              validation.validate.includes('name') ||
              validation.validate.includes('surname') ||
              validation.validate.includes('email') ||
              validation.validate.includes('phone'),
          }">
          {{ __('your_information') }}
        </p>
        <div class="contact-form-box-row">
          <InputComponent
            autocomplete="given-name"
            :placeholder="__('form_name')"
            v-model="contactForm.name"
            @input="validation.removeError('name')"
            :error-message="__(validation.getError('name'))"
            :class="{
              'is-invalid': validation.validate.includes('name'),
            }" />
          <InputComponent
            autocomplete="family-name"
            :placeholder="__('form_surname')"
            v-model="contactForm.surname"
            @input="validation.removeError('surname')"
            :error-message="__(validation.getError('surname'))"
            :class="{
              'is-invalid': validation.validate.includes('surname'),
            }" />
        </div>
        <div class="contact-form-box-row">
          <InputComponent
            autocomplete="email"
            input-type="email"
            inputmode="email"
            :placeholder="__('form_email')"
            v-model="contactForm.email"
            @input="validation.removeError('email')"
            :error-message="__(validation.getError('email'))"
            :class="{
              'is-invalid': validation.validate.includes('email'),
            }" />
          <InputComponent
            :input-type="'tel'"
            inputmode="tel"
            autocomplete="tel"
            :placeholder="__('form_phone')"
            v-model="contactForm.phone"
            @input="validation.removeError('phone')"
            :error-message="__(validation.getError('phone'))"
            :class="{
              'is-invalid': validation.validate.includes('phone'),
            }" />
        </div>
      </div>
      <div class="contact-form-box-section limit-width">
        <p
          class="body-text-bold"
          :class="{
            'error-text': validation.validate.includes('visitReason'),
          }">
          {{ __('form_visit_reason') }}

          <span class="small-list-text-uppercase error-text" v-if="validation.getError('visitReason')">
            {{ __(validation.getError('visitReason')) }}
          </span>
        </p>
        <div class="contact-form-box-section-options">
          <template v-for="option in visitType" :key="option">
            <input
              type="radio"
              :value="option"
              v-model="contactForm.visitReason"
              @change="validation.removeError('visitReason')"
              name="visitReason"
              :id="'visitReason' + option" />
            <label :for="'visitReason' + option">
              {{ __(option) }}
            </label>
          </template>
        </div>
      </div>

      <div class="contact-form-box-section">
        <p
          class="body-text-bold limit-width"
          :class="{
            'error-text': validation.getError('startAt'),
          }">
          {{ __('date_and_time') }}
          <span class="small-list-text-uppercase error-text" v-if="validation.getError('startAt')">
            {{ __(validation.getError('startAt')) }}
          </span>
        </p>
        <div
          class="contact-form-box-section-times"
          :class="{
            'limit-width': !contactForm.visitReason,
          }">
          <template v-if="contactForm.visitReason && !fetchingSlots">
            <p class="small-list-text limit-width">{{ __('desired_date') }}</p>
            <SwiperComponent :items="Object.keys(slots)" children-class=".date-items">
              <template #item="{ item }">
                <div class="date-items">
                  <input
                    type="radio"
                    :value="item.toString()"
                    v-model="contactForm.startAt"
                    :id="'startAt' + item.toString()"
                    @change="validation.removeError('startAt')"
                    name="startAt" />
                  <label :for="'startAt' + item.toString()" class="list-text-bold">
                    {{ QDateTime.parse(item).format('dd.MM.yyyy') }}
                    <p class="small-list-text">
                      {{ __(daysMap[QDateTime.parse(item).weekdayShort?.toString() as keyof typeof daysMap]) }}
                    </p>
                  </label>
                </div>
              </template>
            </SwiperComponent>
          </template>
          <div class="contact-form-box-section-no-time-slots" v-else>
            {{ __('select_reason_to_show_dates') }}
          </div>
        </div>
      </div>
      <div
        class="contact-form-box-section-times"
        v-if="contactForm.visitReason"
        :class="{
          'limit-width': (!store.isMobile && !store.isTablet) || (contactForm.visitReason && !contactForm.startAt),
        }">
        <p
          class="small-list-text"
          :class="{
            'limit-width': store.isMobile || store.isTablet,
          }">
          {{ __('appointment_time') }}
        </p>
        <template v-if="contactForm.startAt && contactForm.visitReason && !fetchingSlots">
          <template v-if="slots[contactForm.startAt] && slots[contactForm.startAt].length">
            <div class="contact-form-box-section-times-box" v-if="!store.isMobile && !store.isTablet">
              <div
                class="contact-form-box-section-times-box-time"
                v-for="(time, index) in slots[contactForm.startAt]"
                :key="index">
                <input
                  type="radio"
                  :value="time.start"
                  :disabled="time.available === false"
                  v-model="contactForm.startAtTime"
                  :id="'startAtTime' + time.start"
                  @change="validation.removeError('startAt')"
                  name="startAtTime" />
                <label :for="'startAtTime' + time.start" class="normal-text-bold">
                  {{ time.start.format('HH:mm') }}
                </label>
              </div>
            </div>
            <SwiperComponent
              :items="slots[contactForm.startAt]"
              children-class=".contact-form-box-section-times-box-time"
              v-else>
              <template #item="{ item }">
                <div class="contact-form-box-section-times-box-time">
                  <input
                    type="radio"
                    :value="item.start"
                    :disabled="item.available === false"
                    v-model="contactForm.startAtTime"
                    :id="'startAtTime' + item.start"
                    @change="validation.removeError('startAt')"
                    name="startAtTime" />
                  <label :for="'startAtTime' + item.start" class="normal-text-bold">
                    {{ item.start.format('HH:mm') }}
                  </label>
                </div>
              </template>
            </SwiperComponent>
          </template>
          <div class="contact-form-box-section-no-time-slots error-text" v-else>
            {{ __('no_appointments_available') }}
          </div>
        </template>
        <div class="contact-form-box-section-no-time-slots" v-else>
          {{ __('select_date_to_show_times') }}
        </div>
      </div>
      <div class="contact-form-box-section limit-width">
        <div class="contact-form-box-row">
          <TextareaComponent
            :place-holder="__('comment_form')"
            v-model="contactForm.comment"
            :class="{
              'is-invalid': validation.validate.includes('comment'),
            }"
            :error-message="__(validation.getError('comment'))"
            @input="validation.removeError('comment')" />
        </div>
      </div>
      <div class="checkbox-column">
        <div class="checkbox">
          <input type="checkbox" id="any_day" :value="true" v-model="contactForm.newsletter" />
          <label for="any_day">
            {{ __('newsletter_text') }}
          </label>
        </div>
        <div
          class="checkbox"
          :class="{
            'is-invalid': validation.validate.includes('terms'),
          }">
          <input type="checkbox" id="terms" :value="true" v-model="contactForm.terms" />
          <label for="terms">
            <p class="list-text" v-html="__('contact_form_legal_text_checkbox')"></p>

            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              @click.prevent="openedTerms = true"
              height="19"
              viewBox="0 0 18 19"
              fill="none">
              <circle cx="9" cy="9.5" r="8.5" stroke="#15BBBD" />
              <path d="M9 4.5V10.5" stroke="#15BBBD" />
              <path d="M9 14.5V12.5" stroke="#15BBBD" />
            </svg>
          </label>
        </div>
      </div>

      <button class="button" type="submit">
        <img src="@/assets/icons/starIcon.svg" />
        {{ __('submit_form') }}
      </button>
    </form>
  </div>

  <div class="modal-background" v-if="slotReservationBooked && contactForm.startAt && contactForm.startAtTime">
    <div class="modal-content" v-click-outside="handleAlreadyBookedReservation">
      <div class="modal-content-header">
        <p class="list-text-bold">{{ __('slot_no_longer_available') }}</p>
        <div class="modal-close" @click="handleAlreadyBookedReservation()">
          <div class="x-icon"></div>
        </div>
      </div>

      <div class="modal-slot-booked-row">
        <div class="modal-slot-booked-column">
          <p class="list-text-bold">
            {{ QDateTime.parse(contactForm.startAt).setZone('Europe/Ljubljana').format('dd.MM') }}
          </p>
          <p class="small-list-text" v-if="contactForm.startAt">
            {{
              __(
                daysMap[
                  QDateTime.parse(contactForm.startAt)
                    .setZone('Europe/Ljubljana')
                    .weekdayShort?.toString() as keyof typeof daysMap
                ]
              )
            }}
          </p>
        </div>
        <div class="modal-slot-booked-column">
          <p class="list-text-bold">
            {{ contactForm.startAtTime }}
          </p>
        </div>
      </div>
      <p class="list-text">
        {{ __('select_another_slot') }}
      </p>

      <button class="button" @click="handleAlreadyBookedReservation">
        <img src="@/assets/icons/starIcon.svg" />
        {{ __('select_new_slot_proceed') }}
      </button>
    </div>
  </div>
  <div class="modal-background" v-if="openedTerms">
    <div
      class="modal-content"
      v-click-outside="
        () => {
          openedTerms = false;
        }
      ">
      <div class="modal-content-header">
        <p class="list-text-bold">
          {{ __('contact_form_legal_text_title') }}
        </p>
        <div class="modal-close" @click="openedTerms = false">
          <div class="x-icon"></div>
        </div>
      </div>
      <div class="modal-content-legal" v-html="__('contact_form_legal_text_content')"></div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { nextTick, reactive, ref, watch } from 'vue';
import InputComponent from '@/components/form-components/InputComponent.vue';
import TextareaComponent from '@/components/form-components/TextareaComponent.vue';
import SwiperComponent from '@/components/SwiperComponent.vue';
import axios from 'axios';
import QDateTime, { DateTime } from '@/QDateTime';
import { generalStore } from '@/store';
import router from '@/router';
import handleValidation from '@/composables/validationHandler';
import __ from '@/translate';

// const firstVisit: Record<number, { start: number; end: number }> = {
//   1: {
//     start: 1530,
//     end: 1700,
//   },
//   2: {
//     start: 1530,
//     end: 1700,
//   },
//   3: {
//     start: 800,
//     end: 1000,
//   },
//   4: {
//     start: 800,
//     end: 1000,
//   },
//   5: {
//     start: 800,
//     end: 1000,
//   },
// };
const openedTerms = ref(false);

type ParsedSlot = {
  start: DateTime;
  end: DateTime;
  available: boolean;
};

type ContactForm = {
  name: string;
  surname: string;
  email: string;
  phone: string;
  visitReason: string;
  startAt: string | null;
  startAtTime: DateTime | null;
  comment: string;
  newsletter: boolean;
  terms: boolean;
};

const daysMap = {
  Mon: 'monday',
  Tue: 'tuesday',
  Wed: 'wednesday',
  Thu: 'thursday',
  Fri: 'friday',
  Sat: 'saturday',
  Sun: 'sunday',
};

const contactForm = ref<ContactForm>({
  name: '',
  surname: '',
  email: '',
  phone: '',
  visitReason: '',
  startAt: '',
  startAtTime: null,
  comment: '',
  newsletter: false,
  terms: false,
});
const validation = reactive(handleValidation<ContactForm>(contactForm));

const visitType = ['visit_type_first_visit', 'visit_type_pain', 'visit_type_tartar_removal', 'visit_type_other'];
const slotReservationBooked = ref(false);
const submitted = ref(false);
const slots = ref<Record<string, ParsedSlot[]>>({});
const fetchingSlots = ref(false);

const store = generalStore();

watch(
  () => contactForm.value.visitReason,
  () => {
    fetchSlots(QDateTime.locationTime());
  }
);

watch(
  () => contactForm.value.startAt,
  (newDate) => {
    if (newDate) {
      contactForm.value.startAtTime = null;
    }
  }
);
// function timeout(ms: number) {
//   return new Promise((resolve) => setTimeout(resolve, ms));
const slotClient = axios.create({
  transformResponse: (data) => {
    return JSON.parse(data, (key, value) => {
      return key === 'start' || key === 'end' ? QDateTime.parse(value).setZone('Europe/Ljubljana') : value;
    });
  },
});
function fetchSlots(date: DateTime) {
  slots.value = {};
  fetchingSlots.value = true;
  slotClient
    .post<Record<string, ParsedSlot[]>>('/api/slots', { startAt: date, visitReason: contactForm.value.visitReason })
    .then((response) => {
      console.log(response.data);
      slots.value = response.data;
      if (
        contactForm.value.startAt &&
        !(slots.value[contactForm.value.startAt] && slots.value[contactForm.value.startAt].length)
      ) {
        contactForm.value.startAt = null;
        contactForm.value.startAtTime = null;
      }
    })
    .catch((reason) => {
      console.log(reason);
    })
    .finally(() => {
      fetchingSlots.value = false;
    });
}
function submitForm(): void {
  if (submitted.value) {
    return;
  }
  // if (!contactForm.value.startAtTime) {
  //   return;
  // }
  const startTime = contactForm.value.startAtTime;
  submitted.value = true;
  axios
    .post('/api/reservations', {
      name: contactForm.value.name,
      surname: contactForm.value.surname,
      email: contactForm.value.email,
      phone: contactForm.value.phone,
      comment: contactForm.value.comment,
      startAt: startTime ? startTime.toString() : null,
      visitReason: contactForm.value.visitReason,
      newsletter: contactForm.value.newsletter,
      terms: contactForm.value.terms,
    })
    .then(() => {
      validation.clearErrors();
      store.submittedData = {
        time: startTime!,
        visitReason: contactForm.value.visitReason,
        comment: contactForm.value.comment,
      };
      contactForm.value = {
        name: '',
        surname: '',
        email: '',
        phone: '',
        visitReason: '',
        startAt: null,
        startAtTime: null,
        comment: '',
        newsletter: false,
        terms: false,
      };
      submitted.value = false;
      router.push({ name: 'SuccessPage' });
    })
    .catch((error) => {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message &&
        error.response.data.message.includes('not available')
      ) {
        slotReservationBooked.value = true;
      }
      validation.setErrors(error.response.data.errors);
      nextTick(() => {
        const firstInvalid = document.querySelector<HTMLElement>('.is-invalid');

        if (firstInvalid) {
          window.scrollTo({
            top: firstInvalid.offsetTop - 120,
            behavior: 'smooth',
          });
        }
      });
      submitted.value = false;
    });
}

function handleAlreadyBookedReservation() {
  slotReservationBooked.value = false;
  contactForm.value.startAtTime = null;
  fetchSlots(QDateTime.locationTime());
}
</script>
